import React, { useState, useEffect } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
// import Decimal from 'decimal.js';
// import { extractNumber } from '@/lib/utils';
import Summary from '@/components/Summary';
import { TotalData, RecommendationsTabKeyNames } from '../../index.type';
import { calculateAdjustment } from './utils';
interface PortfolioAdjustmentProps {
  selectedMetrics: {
    portfolioAdjustment?: boolean;
    [key: string]: boolean | undefined;
  };
  selectChange: (key: string, checked: boolean) => void;
  totalData: Pick<TotalData, RecommendationsTabKeyNames>;
}

const PortfolioAdjustment: React.FC<PortfolioAdjustmentProps> = ({
  selectedMetrics,
  selectChange,
  totalData
}) => {
  const [currentSelectedMetrics, setCurrentSelectedMetrics] = useState<{
    portfolioAdjustment?: boolean;
    [key: string]: boolean | undefined;
  }>({});

  React.useEffect(() => {
    setCurrentSelectedMetrics(selectedMetrics);
  }, [selectedMetrics]);

  // 持仓结构调整建议数据
  const [adjustmentData, setAdjustmentData] = useState<
    {
      category: string;
      currentRatio: string;
      suggestedRatio: string;
      adjustment: string;
      direction: string;
      color: string;
    }[]
  >([
    {
      category: '现金管理',
      currentRatio: '15%',
      suggestedRatio: '25%',
      adjustment: '增加10%',
      direction: 'up',
      color: '#CF1322'
    },
    {
      category: '稳健收益',
      currentRatio: '20%',
      suggestedRatio: '40%',
      adjustment: '增加20%',
      direction: 'up',
      color: '#CF1322'
    },
    {
      category: '进取收益',
      currentRatio: '65%',
      suggestedRatio: '35%',
      adjustment: '减少30%',
      direction: 'down',
      color: '#16A34A'
    }
  ]);

  useEffect(() => {
    const {
      cash_beforeRatio,
      cash_afterRatio,
      cash_trend,
      stable_beforeRatio,
      stable_afterRatio,
      stable_trend,
      aggressive_beforeRatio,
      aggressive_afterRatio,
      aggressive_trend
    } = calculateAdjustment(totalData);

    setAdjustmentData([
      {
        category: '现金管理',
        currentRatio: cash_beforeRatio.toString() + '%',
        suggestedRatio: cash_afterRatio.toString() + '%',
        adjustment: `${cash_trend.greaterThan(0) ? '增加' : '减少'}${cash_trend.abs().toString()}%`,
        direction: cash_trend.greaterThan(0) ? 'up' : 'down',
        color: cash_trend.greaterThan(0) ? '#CF1322' : '#16A34A'
      },
      {
        category: '稳健收益',
        currentRatio: stable_beforeRatio.toString() + '%',
        suggestedRatio: stable_afterRatio.toString() + '%',
        adjustment: `${stable_trend.greaterThan(0) ? '增加' : '减少'}${stable_trend.abs().toString()}%`,
        direction: stable_trend.greaterThan(0) ? 'up' : 'down',
        color: stable_trend.greaterThan(0) ? '#CF1322' : '#16A34A'
      },
      {
        category: '进取收益',
        currentRatio: aggressive_beforeRatio.toString() + '%',
        suggestedRatio: aggressive_afterRatio.toString() + '%',
        adjustment: `${aggressive_trend.greaterThan(0) ? '增加' : '减少'}${aggressive_trend.abs().toString()}%`,
        direction: aggressive_trend.greaterThan(0) ? 'up' : 'down',
        color: aggressive_trend.greaterThan(0) ? '#16A34A' : '#CF1322'
      }
    ]);
  }, [totalData]);

  const handleCheckboxChange = (checked: boolean) => {
    selectChange('portfolioAdjustment', checked);
    setCurrentSelectedMetrics(prev => ({
      ...prev,
      portfolioAdjustment: checked
    }));
  };

  return (
    <div className="my-4 rounded-lg border border-[#F0F0F0] p-3">
      {/* 标题和复选框 */}
      <div className="mb-4 flex items-center gap-2">
        <input
          type="checkbox"
          checked={currentSelectedMetrics.portfolioAdjustment || false}
          onChange={e => handleCheckboxChange(e.target.checked)}
          className="h-4 w-4"
        />
        <span className="text-base font-semibold text-[#1F1F1F]">
          持仓结构调整建议
        </span>
      </div>

      {/* 调整建议表格 */}
      <div className="mb-4 overflow-hidden rounded-lg border border-[#F0F0F0] bg-white">
        <div className="grid grid-cols-4">
          {/* 表头 */}
          <div className="border-b border-r border-[#F0F0F0] bg-[#F0F5FF] p-2 text-center">
            <span className="text-sm font-semibold text-[#1F1F1F]">
              资产类别
            </span>
          </div>
          <div className="border-b border-r border-[#F0F0F0] bg-[#F0F5FF] p-2 text-center">
            <span className="text-sm font-semibold text-[#1F1F1F]">
              当前占比
            </span>
          </div>
          <div className="border-b border-r border-[#F0F0F0] bg-[#F0F5FF] p-2 text-center">
            <span className="text-sm font-semibold text-[#1F1F1F]">
              建议占比
            </span>
          </div>
          <div className="border-b border-[#F0F0F0] bg-[#F0F5FF] p-2 text-center">
            <span className="text-sm font-semibold text-[#1F1F1F]">
              调整方向
            </span>
          </div>

          {/* 数据行 */}
          {adjustmentData.map((item, index) => (
            <React.Fragment key={index}>
              <div className="border-r border-[#F0F0F0] p-2 text-center">
                <span className="text-xs text-[#1F1F1F]">{item.category}</span>
              </div>
              <div className="border-r border-[#F0F0F0] p-2 text-center">
                <span className="text-xs text-[#1F1F1F]">
                  {item.currentRatio}
                </span>
              </div>
              <div className="border-r border-[#F0F0F0] p-2 text-center">
                <span className="text-xs text-[#1F1F1F]">
                  {item.suggestedRatio}
                </span>
              </div>
              <div className="p-2 text-center">
                <div className="flex items-center justify-center gap-1">
                  <span
                    className="whitespace-nowrap text-xs"
                    style={{ color: item.color }}
                  >
                    {item.adjustment}
                  </span>
                  <div className="flex h-4 w-4 items-center justify-center">
                    {item.direction === 'up' ? (
                      <ArrowUp className="h-4 w-4" color={item.color} />
                    ) : (
                      <ArrowDown className="h-4 w-4" color={item.color} />
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* 优化后组合优势卡片 */}
      <div>
        <Summary title="优化后组合优势">
          <div className="flex items-start whitespace-pre-line text-xs leading-relaxed text-[#1F1F1F]">
            <ul className="list-inside list-disc text-left text-sm">
              <li className="h-[25px] font-semibold leading-5">
                {totalData.advantage1_title}
              </li>
              <li className="h-[25px] leading-5">
                {totalData.advantage1_desc}
              </li>
              <li className="h-[25px] font-semibold leading-5">
                {totalData.advantage2_title}
              </li>
              <li className="h-[25px] leading-5">
                {totalData.advantage2_desc}
              </li>
              <li className="h-[25px] font-semibold leading-5">
                {totalData.advantage3_title}
              </li>
              <li className="h-[25px] leading-5">
                {totalData.advantage3_desc}
              </li>
            </ul>
          </div>
        </Summary>
      </div>
    </div>
  );
};

export default PortfolioAdjustment;
